/**
 * O handshake de SSO do iframe do Programa de Indicação.
 *
 * O portal do franqueado (`seazone-franquias`) embute
 * `/parceiros/entrada?target=partner[&tipo=...]` e entrega o token por
 * `postMessage`. O token NUNCA vai na URL: Clarity (inline no `index.html`),
 * GTM/ReactGA (escopo de módulo) e o Sentry Replay leem
 * `document.location.href` antes de o React montar, e o `replaceState` que
 * limparia o fragmento roda depois deles — não há como ganhar essa corrida.
 *
 * Só constantes e funções puras, pelo mesmo motivo do `SpotChoice/caminhos.ts`:
 * a página de entrada é a primeira coisa que carrega dentro do iframe, e o
 * teste do handshake não deve arrastar tela nenhuma.
 */
import { EnumType, partnersRoutes } from '@/pages/Partners/types/paths';
import { LOCATION_ENTRY_PATH } from '@/pages/Partners/utils/locationJourney';

import { SPOT_ENTRY_PATH } from '../Indicate/SpotChoice/caminhos';

/** Marca das mensagens dos dois lados; qualquer outra coisa na janela é ruído. */
export const SSO_MESSAGE_SOURCE = 'seazone-sso';

/**
 * Origens de pai aceitas, lista fechada. O mesmo conjunto que o host serve em
 * `content-security-policy: frame-ancestors` para `/parceiros/*` — a CSP diz
 * quem PODE embutir, esta lista diz de quem aceitamos token.
 */
export const ALLOWED_PARENT_ORIGINS: readonly string[] = [
  'https://franquias.seazone.com.br',
  'https://seazonefranquias.seazone.com.br',
  'https://stg-seazonefranquias.seazone.com.br',
  'http://localhost:8080',
];

/** O único `target` com jornada própria; o resto cai na raiz, como no `UserContext`. */
export const PARTNER_TARGET = 'partner';

const PARTNERS = `/${partnersRoutes.partners.main}`;
const FORM_PATH = `${PARTNERS}/${partnersRoutes.indicate.main}/${partnersRoutes.indicate.form.main}`;

/** Onde `target=partner` aterrissa sem `tipo` — o mesmo destino do fluxo `#token=`. */
export const PARTNER_DEFAULT_PATH = `${PARTNERS}/${partnersRoutes.panel.main}`;

/**
 * Destino por tipo de indicação, explícito.
 *
 * Os nomes de tipo coincidem com os segmentos do formulário, e é justamente por
 * isso que a tabela existe: `locacao` e `spot` têm PORTA própria, e
 * `formulario/locacao` é o formulário legado (`LEGACY_LOCATION_FORM_PATH`)
 * enquanto `formulario/spot` é o wizard de fechamento de contrato
 * (`RESERVE_PATH`), que não é indicação. Derivar o caminho do nome mandaria os
 * dois tipos mais usados para o lugar errado.
 */
export const DESTINATION_BY_TIPO: Readonly<Record<string, string>> = {
  [EnumType.location]: LOCATION_ENTRY_PATH,
  [EnumType.spot]: SPOT_ENTRY_PATH,
  [EnumType.terrain]: `${FORM_PATH}/${EnumType.terrain}`,
  [EnumType.builder]: `${FORM_PATH}/${EnumType.builder}`,
  [EnumType.decor]: `${FORM_PATH}/${EnumType.decor}`,
  [EnumType.franchise]: `${FORM_PATH}/${EnumType.franchise}`,
};

/**
 * Lista fechada de `tipo`: os 6 valores que o letreiro de indicação do portal
 * do franqueado sabe gerar (`ReferralType` em
 * `seazone-franquias/src/components/referral-ticker/phrases.ts`), que são os
 * mesmos 6 cards de `INDICATION_TYPES`. Qualquer outro valor é tratado como
 * ausente: a entrada não é lugar de redirect aberto.
 */
export const SSO_TIPOS: readonly string[] = Object.keys(DESTINATION_BY_TIPO);

/**
 * `tipo` só existe dentro do ramo `target=partner`: fora dele não há jornada de
 * indicação para abrir, e aceitar o parâmetro mesmo assim daria ao pai um
 * segundo jeito de escolher para onde a sessão recém-criada vai.
 */
export const ssoDestination = (target?: string | null, tipo?: string | null): string => {
  if (target !== PARTNER_TARGET) return '/';
  return DESTINATION_BY_TIPO[tipo ?? ''] ?? PARTNER_DEFAULT_PATH;
};

/** O que o filho anuncia quando monta. Não carrega segredo nenhum. */
export const readyMessage = () => ({ source: SSO_MESSAGE_SOURCE, type: 'ready' as const });

interface IncomingMessage {
  origin: string;
  source: MessageEventSource | null;
  data: unknown;
}

/**
 * A mensagem `token` do pai. `expiresIn` é quanto o token ainda dura, em
 * segundos, contado no relógio do pai na hora de enviar: é com ele que a
 * sessão do pai agenda o próximo pedido — o `exp` do JWT é do relógio do IDP,
 * e comparar com o relógio da máquina erra pelo desvio entre os dois.
 */
export interface TokenMessage {
  readonly token: string;
  readonly expiresIn?: number;
}

/**
 * O token da mensagem, ou `null` quando ela não passa em alguma checagem —
 * sempre em silêncio, porque responder a quem falhou é confirmar que aqui tem
 * um handshake escutando.
 *
 * As duas checagens são necessárias: `origin` sozinha não distingue o pai de
 * qualquer outro frame da mesma origem, e `source` sozinha não impede que o pai
 * legítimo tenha sido navegado para outro site.
 */
export const readTokenMessage = (
  event: IncomingMessage,
  parent: Window | null,
): TokenMessage | null => {
  if (!parent || event.source !== parent) return null;
  if (!ALLOWED_PARENT_ORIGINS.includes(event.origin)) return null;

  const data = event.data as {
    source?: unknown;
    type?: unknown;
    token?: unknown;
    expiresIn?: unknown;
  } | null;
  if (!data || typeof data !== 'object') return null;
  if (data.source !== SSO_MESSAGE_SOURCE || data.type !== 'token') return null;
  if (typeof data.token !== 'string' || data.token.trim() === '') return null;

  // `expiresIn` fora do esperado conta como ausente: o pedido cai no `exp` do JWT.
  const { expiresIn } = data;
  const valido = typeof expiresIn === 'number' && Number.isFinite(expiresIn) && expiresIn > 0;
  return { token: data.token, ...(valido && { expiresIn }) };
};
